import SectionTitle from 'components/global/SectionTitle'
import { Button } from '@arckit/ui'
import { Col, Row, Section } from 'components/grid'
import { graphql, Link, useStaticQuery } from 'gatsby'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import React from 'react'

import calculateScrollHeight from '../core-adoption/functions/calculateScrollHeight'
import Resource from '../global/Resource'

interface Props {
	stateReviewsButton?: boolean
	standard?: 'CCSS' | 'TEKS'
	language?: 'english' | 'spanish'
}

export default function ReviewsResults({ stateReviewsButton, standard = 'CCSS', language = 'english' }: Props) {
	const { edReports, essa, ies, plpg, results, trr, knowledge } = useStaticQuery<IQuery>(query)

	return (
		<Section margin="mt-20" id="results" scrollMarginTop={calculateScrollHeight()}>
			<SectionTitle
				testId="reviews-results-section-title"
				title={language === 'english' ? 'Independent Reviews & Results' : 'Reseñas y resultados independientes'}
				text={
					language === 'english'
						? 'Our instructional materials and professional learning services underwent a thorough review process by a national team of experts from EdReports and Rivet Education. ARC Core is backed by research, additional independent state reviews, and implementation results across the country.'
						: 'Nuestros materiales de instrucción y servicios de aprendizaje profesional pasaron por un proceso de revisión exhaustivo por un equipo nacional de expertos de EdReports y Rivet Education. ARC Core está respaldado por la investigación en curso, evaluaciones estatales independientes adicionales y por resultados de implementación en todo el país.'
				}
			/>
			<Row testId="reviews-results-logo-container" center middle className="pb-6">
				{standard === 'TEKS' && (
					<Col width="w-1/3 md:w-1/6" className="mb-4">
						<a href="https://texasresourcereview.org/publishers/american-reading-company" target="_blank" rel="noopener noreferrer">
							<GatsbyImage image={trr.gatsbyImageData} alt="texas resource review badge" />
						</a>
					</Col>
				)}
				<Col width="w-1/3 md:w-1/6" className="mb-4">
					<a href="https://www.edreports.org/reports/overview/arc-american-reading-company-core-2017" target="_blank" rel="noopener noreferrer">
						<GatsbyImage image={edReports.gatsbyImageData} alt="ed reports badge" />
					</a>
				</Col>
				<Col width="w-1/3 md:w-1/6" className="mb-4">
					<a href="https://plpartnerguide.org/partner/american-reading-company/" target="_blank" rel="noopener noreferrer">
						<GatsbyImage image={plpg.gatsbyImageData} alt="plpg badge" />
					</a>
				</Col>
				<Col width="w-1/3 md:w-1/6" className="mb-4">
					<Resource title="IES Study" linkOnly noZoom>
						<GatsbyImage image={ies.gatsbyImageData} alt="ies study badge" />
					</Resource>
				</Col>
				<Col width="w-1/3 md:w-1/6" className="mb-4 px-1">
					<a href="https://www.evidenceforessa.org/program/arc-core-formerly-zoology-one/" target="_blank" rel="noopener noreferrer">
						<GatsbyImage image={essa.gatsbyImageData} alt="essa badge" />
					</a>
				</Col>
				<Col width="w-1/3 md:w-1/6" className="mb-4">
					<Resource title="Results Packet" linkOnly noZoom>
						<GatsbyImage image={results.gatsbyImageData} alt="results packet badge" />
					</Resource>
				</Col>
				<Col width="w-1/3 md:w-1/6" className="mb-4">
					<a href="https://knowledgematterscampaign.org/curriculum/arc-core/" target="_blank" rel="noopener noreferrer">
						<GatsbyImage image={knowledge.gatsbyImageData} alt="knowlege matters badge" />
					</a>
				</Col>
			</Row>
			{stateReviewsButton && (
				<Row className="mt-5">
					<Col className="flex justify-center">
						<Link to="/state-reviews">
							<Button size="lg">{language === 'english' ? 'See State Reviews' : 'Ver evaluaciones estatales'}</Button>
						</Link>
					</Col>
				</Row>
			)}
		</Section>
	)
}

interface IQuery {
	trr: { gatsbyImageData: IGatsbyImageData }
	essa: { gatsbyImageData: IGatsbyImageData }
	edReports: { gatsbyImageData: IGatsbyImageData }
	plpg: { gatsbyImageData: IGatsbyImageData }
	knowledge: { gatsbyImageData: IGatsbyImageData }
	ies: { gatsbyImageData: IGatsbyImageData }
	results: { gatsbyImageData: IGatsbyImageData }
}

const query = graphql`
	query {
		trr: contentfulAsset(title: { eq: "Texas Resource Review Badge" }) {
			gatsbyImageData(height: 210)
		}
		essa: contentfulAsset(title: { eq: "ESSA Badge" }) {
			gatsbyImageData(height: 210)
		}
		edReports: contentfulAsset(title: { eq: "EdReports Badge" }) {
			gatsbyImageData(height: 210)
		}
		plpg: contentfulAsset(title: { eq: "PLPG Badge" }) {
			gatsbyImageData(height: 210)
		}
		ies: contentfulAsset(title: { eq: "IES Study Badge" }) {
			gatsbyImageData(height: 210)
		}
		results: contentfulAsset(title: { eq: "ARC Core Results Badge" }) {
			gatsbyImageData(height: 210)
		}
		knowledge: contentfulAsset(title: { eq: "Knowledge Matters Badge" }) {
			gatsbyImageData(height: 210)
		}
	}
`
